import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  AsyncBox,
  SingleSelect,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  PhoneIcon,
  MailIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import { formatDateDisplayLocal } from '@fortress-technology-solutions/fortress-component-library/utils';
import useIntl from '../../hooks/useIntl';
import messages from './messages';

const Field = ({ label, children }) => (
  <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between' }}>
    <Typography variant="labelSmall" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography variant="bodyMedium" sx={{ textAlign: 'right' }}>
      {children}
    </Typography>
  </Stack>
);

// An activity row whose value opens the edit-activity modal when an activity
// exists (and the user may edit it); otherwise it's plain text (e.g. "None
// scheduled").
const ActivityField = ({ label, text, activity, disabled, onOpen }) => (
  <Stack
    direction="row"
    spacing={1}
    sx={{ justifyContent: 'space-between', alignItems: 'center' }}
  >
    <Typography variant="labelSmall" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    {activity ? (
      <Button
        variant="text"
        size="small"
        onClick={onOpen}
        disabled={disabled}
        sx={{ minWidth: 0, p: 0, textAlign: 'right' }}
      >
        {text}
      </Button>
    ) : (
      <Typography variant="bodyMedium" sx={{ color: 'text.secondary' }}>
        {text}
      </Typography>
    )}
  </Stack>
);

const relativeDays = (intl, iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const days = Math.max(0, Math.floor((Date.now() - then) / 86400000));
  return intl.formatMessage(messages.relativeDays, { days });
};

const LeadCard = ({ lead, intl, assign, activityActions }) => {
  const moveInLabel =
    lead.moveIn?.kind === 'preferred'
      ? intl.formatMessage(messages.preferredMoveIn)
      : intl.formatMessage(messages.scheduledMoveIn);
  const lastActivity = relativeDays(intl, lead.lastActivityAt);

  const callLabel = intl.formatMessage(messages.call);
  const emailLabel = intl.formatMessage(messages.email);

  // Keep the current assignee selectable even if they've dropped out of the
  // assignable set (e.g. lost property access), so the select never blanks.
  const assigneeInList = assign.options.some(
    (option) => option.value === lead.assignedToId,
  );
  const assigneeOptions =
    lead.assignedToId && !assigneeInList
      ? [
          ...assign.options,
          { value: lead.assignedToId, text: lead.assignedTo ?? '' },
        ]
      : assign.options;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        p: 1.5,
        mb: 1.5,
        '&:last-of-type': { mb: 0 },
      }}
    >
      <Stack spacing={1}>
        {/* Name and quick call/email share the top line so a card stays compact. */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography
            variant="titleSmall"
            component={RouterLink}
            to={lead.linkTarget}
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {lead.name}
          </Typography>

          {(lead.phone || lead.email) && (
            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
              {lead.phone && (
                <IconButton
                  size="small"
                  href={`tel:${lead.phone}`}
                  aria-label={callLabel}
                  title={callLabel}
                >
                  <PhoneIcon fontSize="small" />
                </IconButton>
              )}
              {lead.email && (
                <IconButton
                  size="small"
                  href={`mailto:${lead.email}`}
                  aria-label={emailLabel}
                  title={emailLabel}
                >
                  <MailIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          )}
        </Stack>

        {/* Assigned to: inline (re)assignment. Read-only name without the
            prospect-assign permission. */}
        <Box>
          <Typography
            variant="labelSmall"
            sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
          >
            {intl.formatMessage(messages.assignedTo)}
          </Typography>
          <SingleSelect
            options={assigneeOptions}
            value={lead.assignedToId}
            fullWidth
            sx={{ height: 40 }}
            disabled={!assign.canAssign || assign.assigningLeadId === lead.leadId}
            placeholder={intl.formatMessage(messages.unassigned)}
            onChange={(value) => assign.onAssign(lead.leadId, value)}
          />
        </Box>

        <Stack spacing={0.5}>
          {lead.moveIn?.date && (
            <Field label={moveInLabel}>
              {formatDateDisplayLocal(lead.moveIn.date)}
            </Field>
          )}

          {lead.unit && (
            <Field label={intl.formatMessage(messages.unit)}>{lead.unit}</Field>
          )}

          <Field label={intl.formatMessage(messages.feesPaid)}>
            {lead.feesPaid
              ? intl.formatMessage(messages.yes)
              : intl.formatMessage(messages.no)}
          </Field>

          {/* Gate on the derived relative time (from lastActivityAt) so the row
              still shows when the API sends only the timestamp; ActivityField
              makes it clickable only when the full activity object is present. */}
          {lastActivity && (
            <ActivityField
              label={intl.formatMessage(messages.lastActivity)}
              text={lastActivity}
              activity={lead.lastActivity}
              disabled={activityActions.isDisabled}
              onOpen={() => activityActions.onOpen(lead.lastActivity, lead)}
            />
          )}

          {/* Next activity ALWAYS renders — nothing scheduled means nobody is
              driving the lead, which is exactly what the user needs to see. */}
          <ActivityField
            label={intl.formatMessage(messages.nextActivity)}
            text={
              lead.nextActivityAt
                ? formatDateDisplayLocal(lead.nextActivityAt)
                : intl.formatMessage(messages.noneScheduled)
            }
            activity={lead.nextActivity}
            disabled={activityActions.isDisabled}
            onOpen={() => activityActions.onOpen(lead.nextActivity, lead)}
          />
        </Stack>
      </Stack>
    </Box>
  );
};

const StageLeadsPanel = ({ selectedStage, leads, assign, activityActions }) => {
  const intl = useIntl();

  if (!selectedStage) {
    return (
      <Box
        sx={{
          p: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Typography variant="bodyMedium" sx={{ color: 'text.secondary' }}>
          {intl.formatMessage(messages.panelPrompt)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="titleSmall">{selectedStage.label}</Typography>
        <Typography variant="labelSmall" sx={{ color: 'text.secondary' }}>
          {intl.formatMessage(messages.panelCount, { count: leads.total })}
        </Typography>
      </Box>
      {/* Bounded, scrollable list: a full stage can hold dozens of leads, so the
          cards scroll within the panel instead of stretching the page. */}
      <Box sx={{ p: 2, maxHeight: { xs: 'none', md: '70vh' }, overflowY: 'auto' }}>
        <AsyncBox loading={leads.isLoading}>
          {leads.isError ? (
            <Typography variant="bodyMedium" sx={{ color: 'error.main' }}>
              {intl.formatMessage(messages.panelError)}
            </Typography>
          ) : leads.data.length === 0 ? (
            <Typography variant="bodyMedium" sx={{ color: 'text.secondary' }}>
              {intl.formatMessage(messages.panelEmpty)}
            </Typography>
          ) : (
            leads.data.map((lead) => (
              <LeadCard
                key={lead.leadId}
                lead={lead}
                intl={intl}
                assign={assign}
                activityActions={activityActions}
              />
            ))
          )}
        </AsyncBox>
      </Box>

      {!leads.isLoading && !leads.isError && leads.data.length > 0 && (
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Typography variant="labelSmall" sx={{ color: 'text.secondary' }}>
              {intl.formatMessage(messages.showingCount, {
                shown: leads.data.length,
                total: leads.total,
              })}
            </Typography>
            {leads.hasMore && (
              <Button
                size="small"
                variant="text"
                onClick={leads.onLoadMore}
                disabled={leads.isFetchingMore}
              >
                {intl.formatMessage(messages.loadMore)}
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default StageLeadsPanel;
